import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { STATUS_LABELS } from "@/lib/i18n";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2 } from "lucide-react";

const STATUSES = ["EN ATTENTE DE PAIEMENT", "PAYEE", "EN COURS DE PRODUCTION", "LIVREE"];
const empty = { client_id: "", package_id: "", status: "PAYEE" };

export default function AdminAllOrders() {
  const { t, lang, money } = useLang();
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [packs, setPacks] = useState([]);
  const [form, setForm] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    Promise.all([api.get("/orders"), api.get("/clients"), api.get("/packages")])
      .then(([o, c, p]) => { setOrders(o.data); setClients(c.data); setPacks(p.data); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm({ ...empty }); };
  const openEdit = (o) => { setEditing(o); setForm({ client_id: o.client_id, package_id: o.package_id, status: o.status }); };

  const save = async () => {
    if (!form.client_id || !form.package_id) return toast.error(t("ord.need_fields"));
    try {
      if (editing) { await api.put(`/orders/${editing.id}`, form); toast.success(t("ord.updated")); }
      else { await api.post("/orders", form); toast.success(t("ord.created")); }
      setForm(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const del = async (o) => {
    if (!window.confirm(t("ord.del_confirm"))) return;
    await api.delete(`/orders/${o.id}`); toast.success(t("ord.deleted")); load();
  };

  return (
    <div className="ch-fade-up space-y-8">
      <header className="flex items-end justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-neutral-400">{t("ord.eyebrow")}</p>
          <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight">{t("nav.orders")}</h1>
        </div>
        <Button data-testid="new-order-button" onClick={openNew} className="rounded-sm bg-black hover:bg-neutral-800">
          <Plus className="mr-2 h-4 w-4" /> {t("ord.new")}
        </Button>
      </header>

      <div className="overflow-hidden rounded-sm border border-neutral-200">
        <div className="hidden grid-cols-12 gap-4 border-b border-neutral-200 bg-neutral-50 px-6 py-3 text-xs font-semibold uppercase tracking-wider text-neutral-400 md:grid">
          <div className="col-span-3">{t("ord.client")}</div>
          <div className="col-span-3">{t("ao.col_pack")}</div>
          <div className="col-span-2">{t("common.status")}</div>
          <div className="col-span-2 text-right">{t("common.price")}</div>
          <div className="col-span-2 text-right">{t("common.actions")}</div>
        </div>
        {orders.map((o) => (
          <div key={o.id} data-testid={`all-order-row-${o.id}`} className="grid grid-cols-1 gap-2 border-b border-neutral-100 px-6 py-4 last:border-0 md:grid-cols-12 md:items-center md:gap-4">
            <div className="col-span-3 font-semibold">{o.client_name}</div>
            <div className="col-span-3 text-sm text-neutral-600">{o.package_name}</div>
            <div className="col-span-2"><StatusBadge status={o.status} /></div>
            <div className="col-span-2 font-display font-bold md:text-right">{money(o.price)}</div>
            <div className="col-span-2 flex gap-1 md:justify-end">
              <button data-testid={`edit-order-${o.id}`} onClick={() => openEdit(o)} className="rounded-sm p-1.5 hover:bg-neutral-100"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`delete-order-${o.id}`} onClick={() => del(o)} className="rounded-sm p-1.5 text-neutral-500 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
        {orders.length === 0 && <div className="px-6 py-12 text-center text-sm text-neutral-400">{t("ord.empty")}</div>}
      </div>

      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent data-testid="order-form-dialog" className="rounded-sm">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? t("ord.edit") : t("ord.new")}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>{t("ord.client")}</Label>
                <Select value={form.client_id} onValueChange={(v) => setForm((f) => ({ ...f, client_id: v }))}>
                  <SelectTrigger data-testid="order-client-select" className="rounded-sm"><SelectValue placeholder={t("ord.pick_client")} /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.prenom} {c.nom}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("ord.pack")}</Label>
                <Select value={form.package_id} onValueChange={(v) => setForm((f) => ({ ...f, package_id: v }))}>
                  <SelectTrigger data-testid="order-package-select" className="rounded-sm"><SelectValue placeholder={t("ord.pick_pack")} /></SelectTrigger>
                  <SelectContent>{packs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {money(p.price)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("common.status")}</Label>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="order-status-form-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[lang]?.[s] || s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setForm(null)} className="rounded-sm">{t("common.cancel")}</Button>
            <Button data-testid="save-order-button" onClick={save} className="rounded-sm bg-black hover:bg-neutral-800">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
